import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Type definitions for sentiment data from make.com
export interface SentimentData {
  sentiment_usd: {
    percent_bullish: number;
    percent_bearish: number;
    relevant_tweet_count: number;
    relevant_news_count?: number;  // New field for news articles
    total_relevant_count?: number; // Combined total
  };
  sentiment_xau: {
    percent_bullish: number;
    percent_bearish: number;
    relevant_tweet_count: number;
    relevant_news_count?: number;  // New field for news articles
    total_relevant_count?: number; // Combined total
  };
  // Optional metadata about the analysis
  analysis_metadata?: {
    tweet_count: number;
    news_count: number;
    timestamp: string;
  };
}

// Interface for database records
export interface SentimentRecord {
  id?: string;
  created_at?: string;
  sentiment_data: SentimentData;
  date_analyzed: string;
}

// For backward compatibility with existing code
export interface SentimentEntry {
  id?: string;
  created_at?: string;
  source: string;
  text_content?: string;
  sentiment_score: number;
  entities?: string[];
  date_analyzed: string;
}

// Sentiment analysis service
export const SentimentService = {
  // Get the latest sentiment data
  getLatestSentimentData: async (): Promise<SentimentData | null> => {
    const { data, error } = await supabase
      .from('sentiment_records')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1);
      
    if (error) {
      console.error('Error fetching sentiment data:', error);
      return null;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    // Handle the case where sentiment_data might be a string
    let sentimentData = data[0].sentiment_data;
    if (typeof sentimentData === 'string') {
      try {
        sentimentData = JSON.parse(sentimentData);
      } catch (e) {
        console.error('Error parsing sentiment data string:', e);
        return null;
      }
    }
    
    return sentimentData;
  },
  
  // Get sentiment history for a specific time period (in days)
  getSentimentHistory: async (days: number = 30): Promise<SentimentRecord[]> => {
    const dateFilter = new Date();
    dateFilter.setDate(dateFilter.getDate() - days);
    
    const { data, error } = await supabase
      .from('sentiment_records')
      .select('*')
      .gte('created_at', dateFilter.toISOString())
      .order('created_at', { ascending: true });
      
    if (error) {
      console.error('Error fetching sentiment history:', error);
      return [];
    }
    
    return data || [];
  },
  
  // Store new sentiment data from make.com
  storeSentimentData: async (sentimentData: SentimentData): Promise<boolean> => {
    const record: SentimentRecord = {
      sentiment_data: sentimentData,
      date_analyzed: new Date().toISOString()
    };
    
    const { error } = await supabase
      .from('sentiment_records')
      .insert([record]);
      
    if (error) {
      console.error('Error storing sentiment data:', error);
      return false;
    }
    
    return true;
  },
  
  // For backward compatibility with SentimentForm component
  addSentimentData: async (sentimentEntry: Omit<SentimentEntry, 'id' | 'created_at'>): Promise<boolean> => {
    const { error } = await supabase
      .from('sentiment_entries')
      .insert([sentimentEntry]);
      
    if (error) {
      console.error('Error adding sentiment entry:', error);
      return false;
    }
    
    return true;
  }
};